import { useEffect, useState } from 'react'

import Modal from 'components/elements/Modal'
import SimpleSpinner from 'components/elements/SimpleSpinner'
import PhatButton, { ButtonColor } from 'components/formElements/PhatButton'

import styles from './confirmDeleteModal.module.scss'

type Props = {
  open: boolean
  onClose: () => void
  header?: string
  message?: React.ReactNode
  buttonText?: string
  loadingMessage?: string
  delayEnabledTime?: number // ms to wait before enabling the button
  modelClassName?: string
  confirmButtonColor?: ButtonColor
} & ({ onDelete: () => Promise<any>; onConfirm?: never } | { onDelete?: never; onConfirm: () => void })

const ConfirmDeleteModal = ({
  open,
  onClose,
  onDelete,
  onConfirm,
  header = 'Delete thing',
  message = 'Are you sure you want to delete that thing?',
  buttonText = 'Confirm delete',
  loadingMessage = 'Deleting that thing...',
  delayEnabledTime = 0,
  modelClassName = '',
  confirmButtonColor,
}: Props) => {
  const [buttonEnabled, setButtonEnabled] = useState(false)
  const [loading, setLoading] = useState(false)

  const handleConfirmDelete = () => {
    if (onConfirm) return onConfirm()

    setLoading(true)

    onDelete()
      .then(() => setButtonEnabled(false))
      .then(onClose)
      // .catch handled by the parent
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (open) setTimeout(() => setButtonEnabled(true), delayEnabledTime)
    else setButtonEnabled(false)
  }, [open])

  return (
    <Modal open={open} onClose={onClose} closeButton modalClassName={`${styles.modal} ${modelClassName}`}>
      <h2>{header}</h2>

      <div className={styles.info}>{message}</div>

      <PhatButton
        color={confirmButtonColor || 'red'}
        disabled={!buttonEnabled || loading}
        onClick={handleConfirmDelete}>
        {!loading && <>{buttonText}</>}
        {loading && (
          <>
            <SimpleSpinner size="xSmall" /> {loadingMessage}
          </>
        )}
      </PhatButton>
    </Modal>
  )
}

export default ConfirmDeleteModal
